import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeftRight, Home, Dumbbell, Building2, Check, X, ChevronDown, ChevronUp 
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExerciseSwapper = ({ drill, onSwap, currentSwap }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!drill.alternatives || drill.alternatives.length === 0) {
    return null;
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'home': return <Home className="w-4 h-4" />;
      case 'minimal': return <Dumbbell className="w-4 h-4" />;
      case 'gym': return <Building2 className="w-4 h-4" />;
      default: return <Home className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'home': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'minimal': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'gym': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const handleSwap = async (alternative) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/exercise-preferences/swap`, {
        original: drill.name,
        alternative: alternative.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Swapped to ${alternative.name}`);
      if (onSwap) onSwap(drill.name, alternative.name);
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/exercise-preferences/swap/${encodeURIComponent(drill.name)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Reset to ${drill.name}`);
      if (onSwap) onSwap(drill.name, null);
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const isSwapped = currentSwap && currentSwap !== drill.name;

  return (
    <div className="mt-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`text-xs ${isSwapped ? 'text-accent' : 'text-muted-foreground'} hover:text-white`}
      >
        <ArrowLeftRight className="w-3 h-3 mr-1" />
        {isSwapped ? `Using: ${currentSwap}` : 'Swap Exercise'}
        {isOpen ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
      </Button>

      {isOpen && (
        <Card className="mt-2 bg-background/80 border-border">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground mb-2">
              Pick an alternative based on your equipment:
            </div>
            
            <div className="space-y-2">
              {/* Original exercise option */}
              <div 
                className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-all
                  ${!isSwapped ? 'bg-primary/20 border border-primary/50' : 'bg-muted/20 hover:bg-muted/40'}`}
                onClick={isSwapped ? handleReset : undefined}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-white">{drill.name}</span>
                  <Badge className="bg-purple-500/20 text-purple-400 text-xs">Original</Badge>
                </div>
                {!isSwapped && <Check className="w-4 h-4 text-primary" />}
              </div>

              {/* Alternative options */}
              {drill.alternatives.map((alt, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-all
                    ${currentSwap === alt.name ? 'bg-accent/20 border border-accent/50' : 'bg-muted/20 hover:bg-muted/40'}`}
                  onClick={() => !saving && handleSwap(alt)}
                >
                  <div className="flex items-center gap-2">
                    {getTypeIcon(alt.type)}
                    <span className="text-sm text-white">{alt.name}</span>
                    <Badge className={`text-xs ${getTypeColor(alt.type)}`}>
                      {alt.type === 'home' ? 'No Equipment' : alt.equipment}
                    </Badge>
                  </div>
                  {currentSwap === alt.name && <Check className="w-4 h-4 text-accent" />}
                </div>
              ))}
            </div>

            <div className="mt-3 pt-2 border-t border-border text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1"><Home className="w-3 h-3 text-green-400" /> Home</span>
                <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3 text-blue-400" /> Minimal</span>
                <span className="flex items-center gap-1"><Building2 className="w-3 h-3 text-purple-400" /> Gym</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExerciseSwapper;
